/**
 * Dock uses Cloudflare `useAgent` + `useAgentChat` (`@cloudflare/ai-chat/react`).
 * STACK.md specifies `@ai-sdk/react` `useChat` for the same AI Elements chrome.
 * Same UI message shape; different hook. Do not add Think DO / getSandbox wiring here.
 *
 * Shell (48px rail / 320px panel, chips, deep-links) must render even when
 * the chat session errors — live chat is isolated behind an error boundary.
 *
 * M5: the whole dock is a zip drop zone. Staging is POST …/hardware/uploads;
 * the sent file part is pcbx-artifact://art_… — Think rewrites that to an
 * artifact reference and calls import_project.
 */
import { useAgentChat } from "@cloudflare/ai-chat/react";
import { thinkChatBodyForMode, type PcbxTurnMode } from "@/lib/think-turn-mode";
import { thinkChatBodyViewRev } from "@/lib/think-view-rev";
import {
  Conversation,
  ConversationContent,
  ConversationEmptyState,
  ConversationScrollButton,
} from "@/components/ai-elements/conversation";
import {
  Message,
  MessageContent,
  MessageResponse,
} from "@/components/ai-elements/message";
import {
  PromptInput,
  PromptInputBody,
  PromptInputFooter,
  PromptInputProvider,
  PromptInputSubmit,
  PromptInputTextarea,
  usePromptInputAttachments,
} from "@/components/ai-elements/prompt-input";
import {
  ChainOfThought,
  ChainOfThoughtContent,
  ChainOfThoughtHeader,
  ChainOfThoughtStep,
} from "@/components/ai-elements/chain-of-thought";
import { Reasoning, ReasoningContent, ReasoningTrigger } from "@/components/ai-elements/reasoning";
import { ErrorBoundary } from "@/components/error-boundary";
import { PageAgentBar } from "@/components/page-agent-bar";
import { ThinkDropOverlay } from "@/components/think-drop-overlay";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { JobCard, JobWakeMarker, ThinkJobsPill, scrollToJobCard } from "@/components/job-card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

import { HARDWARE_RELOAD_EVENT, listJobs, stageHardwareZip } from "@/lib/api";
import {
  clampThinkWidth,
  readThinkWidthPx,
  THINK_DOCK,
  thinkOpenWidth,
  thinkWidthAfterArrow,
} from "@/lib/think-dock-layout";
import {
  applyThinkDropRejection,
  attachThinkDropSurface,
  dedupeThinkZipChips,
  formatUploadProgress,
  listenThinkZipRejection,
  reportThinkZipRejection,
  THINK_DROP_REJECT,
  THINK_HINT_COLOR,
  THINK_OK,
  THINK_REJECT_BG,
  THINK_REJECT_BORDER,
  THINK_REJECT_TEXT,
  THINK_ZIP_ACCEPT,
  THINK_ZIP_MAX_BYTES,
  THINK_ZIP_PICKER_LABEL,
  thinkComposerStatus,
  thinkDropHoverName,
  thinkFilePart,
  validateThinkZipFile,
  type ThinkDropInputState,
} from "@/lib/think-drop";
import {
  consumeThinkPickerPending,
  decideThinkPickerAction,
  isThinkPickerPending,
  subscribeThinkPickerPending,
  thinkPickerGestureFromEvent,
  THINK_ZIP_PICKER_CTA,
  THINK_ZIP_PICKER_CTA_HINT,
} from "@/lib/think-picker-request";
import {
  clearStagedThinkZip,
  getStagedThinkZip,
  setStagedThinkZip,
  subscribeStagedThinkZip,
  takeThinkSendFileParts,
  thinkStagedZipKey,
  type StagedThinkZip,
} from "@/lib/think-staged-zip";
import {
  createThinkMissionDriveSession,
  endMissionDrive,
  formatMissionCall,
  getMissionDrive,
  driveFromAgentState,
  MISSION_CANCEL_EVENT,
  MISSION_PAUSE_EVENT,
  MISSION_RESUME_EVENT,
  lastMessageOnlyPrepareSend,
  officialThinkIsBusy,
  invokeOfficialThinkStop,
  isThinkTurnStopped,
  registerOfficialThinkStop,
  requestCancelMissionDrive,
  resumeMissionDrive,
  sendOfficialChatCancel,
  setLiveThinkAgent,
} from "@/lib/mission-drive";

/** Server get-messages page size. Do not load-all then slice on the client. */
export const THINK_VISIBLE_MESSAGE_COUNT = 16;

function yieldPaint(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof requestAnimationFrame === "function") {
      requestAnimationFrame(() => resolve());
      return;
    }
    setTimeout(resolve, 0);
  });
}

function qaR16StopTrace(event: string, detail: Record<string, unknown>): void {
  if (typeof window === "undefined") return;
  const flag = (window as unknown as { __qaR16Trace?: boolean; __qaR15Trace?: boolean }).__qaR16Trace
    ?? (window as unknown as { __qaR15Trace?: boolean }).__qaR15Trace;
  if (!flag) return;
  console.info("[qa-r16-stop]", event, detail);
}
import {
  MISSION_NAV_CLIENT_TOOL_NAMES,
  MissionNavigator,
  createBrowserMissionDeps,
  type AskUserPending,
} from "@/lib/mission-navigator";
import { readWorkbenchSelection } from "@/lib/selection-store";
import { THINK_AGENT, rememberThinkProjectId, thinkAgentHost, thinkAgentName } from "@/lib/think-agent";
import {
  backfillOlderPagesUntilConnected,
  flattenOlderPages,
  hasMoreFromThinkHeader,
  olderPagesCursor,
  prependOlderPage,
} from "@/lib/think-history-window";
import { scheduleThinkHydrate, thinkSessionMounts } from "@/lib/think-hydrate";
import { fetchThinkMessagesPage } from "@/lib/think-messages";
import { useProjectScope } from "@/lib/project-scope";
import {
  cancelViaThinkText,
  collectJobsFromParts,
  isActiveJobState,
  isInternalWakeText,
  isThinkJobToolName,
  jobToolNameFromPart,
  chatTurnErrorFromAgentState,
  hydrateFromAgentState,
  jobsFromAgentState,
  unionThinkJobs,
  parsePcbxJobPart,
  thinkJobsFromWorkbenchItems,
  resolveJobForCard,
  canShowNeedsHumanRetry,
  loadNeedsHumanConsumed,
  needsHumanConsumedFromMessages,
  persistNeedsHumanConsumed,
  resumeWriteViaThinkText,
  retryViaThinkText,
  sortJobsForPill,
  type ThinkJob,
} from "@/lib/think-jobs";
import { surfaceNonJobThinkToolPart } from "@/lib/think-tool-surface";
import { OPEN_THINK_PICKER_EVENT } from "@/lib/underline-nav";
import { cn } from "@/lib/utils";
import { useAgent } from "agents/react";
import { Check, Paperclip, XIcon } from "lucide-react";
import {
  createContext,
  startTransition,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type Dispatch,
  type DragEvent,
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode,
  type SetStateAction,
} from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

const ThinkRejectContext = createContext<{
  state: ThinkDropInputState;
  setState: Dispatch<SetStateAction<ThinkDropInputState>>;
} | null>(null);

function useThinkReject() {
  const ctx = useContext(ThinkRejectContext);
  if (!ctx) {
    throw new Error("useThinkReject must be used inside ThinkDropZone");
  }
  return ctx;
}

const CHIP_PROMPTS = [
  { label: "Upload hardware zip", to: "files#upload" },
  { label: "Start design review", to: "changes?new=1" },
  { label: "Explain this finding", to: "explain" },
] as const;

function useStagedThinkZip(org?: string, project?: string): StagedThinkZip | null {
  return useSyncExternalStore(
    subscribeStagedThinkZip,
    () => (org && project ? getStagedThinkZip(org, project) : null),
    () => (org && project ? getStagedThinkZip(org, project) : null),
  );
}

export function AgentDock({
  open = false,
  onOpenChange,
  fill = false,
}: {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Sheet surface: fill the portal panel. Do not inherit :root --think-w (48px). */
  fill?: boolean;
}) {
  const { orgSlug, projectSlug } = useParams();
  const providerKey = orgSlug && projectSlug ? thinkStagedZipKey(orgSlug, projectSlug) : "think";
  const [jobs, setJobs] = useState<ThinkJob[]>([]);
  const onSelectJob = useCallback((job: ThinkJob) => {
    scrollToJobCard(job.id);
  }, []);
  const expanded = open || fill;
  return (
    <PromptInputProvider key={providerKey}>
      <aside
        className="relative flex h-full min-h-0 w-full shrink-0 flex-col overflow-hidden border-l border-border bg-card transition-[width] duration-150 ease-out"
        style={{ width: fill ? "100%" : open ? "var(--think-w, 320px)" : THINK_DOCK.collapsed }}
        data-testid="think-dock"
        data-think-surface={fill ? "sheet" : "dock"}
      >
        <ThinkDropZone>
          {open && !fill ? <ThinkResizeHandle /> : null}
          <ThinkPickerBridge open={open} onOpen={() => onOpenChange?.(true)} />
          <div className={cn("h-full", expanded && "hidden")}>
            <ThinkRail
              onExpand={() => onOpenChange?.(true)}
              jobs={jobs}
              onSelectJob={onSelectJob}
            />
          </div>
          <div className={cn("flex h-full min-h-0 w-full flex-col", !expanded && "hidden")}>
            <ThinkExpanded
              onCollapse={() => onOpenChange?.(false)}
              jobs={jobs}
              onJobsChange={setJobs}
              onSelectJob={onSelectJob}
            />
          </div>
        </ThinkDropZone>
      </aside>
    </PromptInputProvider>
  );
}

function ThinkResizeHandle() {
  const [show, setShow] = useState(false);
  const [value, setValue] = useState<number>(THINK_DOCK.expandedDefault);

  const readCurrent = () => {
    const shell = document.querySelector(".app-shell") as HTMLElement | null;
    const inline = shell?.style.getPropertyValue("--think-w");
    const computed = shell ? getComputedStyle(shell).getPropertyValue("--think-w") : "";
    return readThinkWidthPx(inline || computed, thinkOpenWidth(window.innerWidth));
  };

  const applyWidth = (next: number) => {
    const shell = document.querySelector(".app-shell") as HTMLElement | null;
    if (!shell) return;
    const clamped = clampThinkWidth(next, window.innerWidth);
    shell.style.setProperty("--think-w", `${clamped}px`);
    setValue(clamped);
  };

  useEffect(() => {
    const mq = window.matchMedia(`(min-width: ${THINK_DOCK.dragAt}px)`);
    const sync = () => {
      setShow(mq.matches);
      if (mq.matches) setValue(readCurrent());
    };
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);
  if (!show) return null;

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    const shell = event.currentTarget.closest(".app-shell") as HTMLElement | null;
    if (!shell) return;
    const handle = event.currentTarget;
    handle.setPointerCapture(event.pointerId);
    const move = (ev: globalThis.PointerEvent) => {
      applyWidth(window.innerWidth - ev.clientX);
    };
    const up = () => {
      handle.releasePointerCapture(event.pointerId);
      handle.removeEventListener("pointermove", move);
      handle.removeEventListener("pointerup", up);
    };
    handle.addEventListener("pointermove", move);
    handle.addEventListener("pointerup", up);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (
      event.key !== "ArrowLeft" &&
      event.key !== "ArrowRight" &&
      event.key !== "ArrowUp" &&
      event.key !== "ArrowDown"
    ) {
      return;
    }
    event.preventDefault();
    applyWidth(thinkWidthAfterArrow(readCurrent(), event.key, window.innerWidth));
  };

  return (
    <div
      role="separator"
      tabIndex={0}
      aria-orientation="vertical"
      aria-valuenow={value}
      aria-valuemin={THINK_DOCK.min}
      aria-valuemax={THINK_DOCK.max}
      aria-label="Resize Think"
      data-testid="think-resize-handle"
      className="absolute inset-y-0 left-0 z-20 w-1.5 cursor-col-resize focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#C45C26]"
      onPointerDown={onPointerDown}
      onKeyDown={onKeyDown}
    />
  );
}

function ThinkPickerBridge({ open, onOpen }: { open: boolean; onOpen: () => void }) {
  const attachments = usePromptInputAttachments();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onPicker = (event: Event) => {
      onOpen();
      const action = decideThinkPickerAction({
        pending: isThinkPickerPending(),
        sameGesture: thinkPickerGestureFromEvent(event),
        fileInputReady: Boolean(inputRef.current),
      });
      if (action === "open-dialog") {
        consumeThinkPickerPending();
        inputRef.current?.click();
      }
    };
    window.addEventListener(OPEN_THINK_PICKER_EVENT, onPicker);
    return () => window.removeEventListener(OPEN_THINK_PICKER_EVENT, onPicker);
  }, [onOpen]);

  return (
    <input
      ref={inputRef}
      type="file"
      accept={THINK_ZIP_ACCEPT}
      className="sr-only"
      tabIndex={-1}
      aria-hidden
      data-testid="think-zip-file-input"
      onChange={(event) => {
        const files = [...(event.currentTarget.files ?? [])];
        event.currentTarget.value = "";
        const file = files[0];
        if (!file) return;
        const checked = validateThinkZipFile(file);
        if (!checked.ok) {
          reportThinkZipRejection(checked.message);
          return;
        }
        attachments.add([file]);
      }}
    />
  );
}

function ThinkZipPicker() {
  const attachments = usePromptInputAttachments();
  const btnRef = useRef<HTMLButtonElement>(null);
  const [awaiting, setAwaiting] = useState(() => isThinkPickerPending());

  const applyPending = useCallback(
    (sameGesture: boolean) => {
      if (!isThinkPickerPending()) return;
      const action = decideThinkPickerAction({
        pending: true,
        sameGesture,
        fileInputReady: Boolean(attachments.fileInputRef.current),
      });
      consumeThinkPickerPending();
      if (action === "open-dialog") {
        attachments.openFileDialog();
        setAwaiting(false);
        return;
      }
      setAwaiting(true);
      btnRef.current?.focus();
    },
    [attachments],
  );

  useEffect(() => {
    applyPending(false);
    return subscribeThinkPickerPending(() => applyPending(false));
  }, [applyPending]);

  return (
    <div className="flex min-w-0 flex-col items-start">
      <button
        ref={btnRef}
        type="button"
        className={cn(
          "inline-flex max-w-full items-center gap-1 text-[11px] hover:text-foreground",
          awaiting && "rounded-sm px-0.5 text-foreground outline outline-1 outline-[#C45C26]",
        )}
        style={awaiting ? undefined : { color: THINK_HINT_COLOR }}
        data-testid="think-zip-picker"
        data-think-picker-cta={awaiting ? "true" : undefined}
        data-mission-danger=""
        onClick={() => {
          setAwaiting(false);
          attachments.openFileDialog();
        }}
      >
        <Paperclip className="size-3 shrink-0" />
        <span className="truncate">{awaiting ? THINK_ZIP_PICKER_CTA : THINK_ZIP_PICKER_LABEL}</span>
      </button>
      <span
        className="max-w-full text-[11px] leading-snug"
        style={{ color: THINK_HINT_COLOR }}
        data-testid={awaiting ? "think-zip-picker-cta" : undefined}
      >
        {awaiting ? THINK_ZIP_PICKER_CTA_HINT : null}
      </span>
    </div>
  );
}

function useProjectBase() {
  const { orgSlug, projectSlug } = useParams();
  if (orgSlug && projectSlug) return `/o/${orgSlug}/p/${projectSlug}`;
  if (orgSlug) return `/o/${orgSlug}`;
  return "/";
}

function useThinkHrefs() {
  const base = useProjectBase();
  const { reviewId } = useParams();
  const location = useLocation();
  const reviewHref = reviewId
    ? `${base}/changes/${reviewId}${location.search}`
    : `${base}/changes`;

  return {
    hardware: `${base}/files`,
    upload: `${base}/files#upload`,
    reviews: `${base}/changes?new=1`,
    review: reviewHref,
  };
}

function DeepLinks({ className }: { className?: string }) {
  const hrefs = useThinkHrefs();
  return (
    <div className={cn("flex flex-wrap gap-1.5", className)}>
      <Button size="sm" variant="outline" className="h-7 px-3 text-[11px]" asChild>
        <Link to={hrefs.hardware}>Open Files</Link>
      </Button>
      <Button size="sm" className="h-7 px-3 text-[11px]" asChild>
        <Link to={hrefs.review}>Open this review</Link>
      </Button>
    </div>
  );
}

function SuggestionChips({ onUploadZip }: { onUploadZip?: () => void }) {
  const hrefs = useThinkHrefs();
  const map = useMemo(
    () => ({
      "files#upload": hrefs.upload,
      "changes?new=1": hrefs.reviews,
      explain: hrefs.review,
    }),
    [hrefs],
  );

  return (
    <div className="flex flex-wrap justify-start gap-1.5">
      {CHIP_PROMPTS.map((chip) =>
        chip.to === "files#upload" && onUploadZip ? (
          <button
            key={chip.label}
            type="button"
            className="rounded-full border border-border bg-transparent px-2.5 py-1 text-[11px] tracking-[0.02em] text-muted-foreground hover:border-white/25 hover:bg-white/[0.08] hover:text-foreground"
            onClick={onUploadZip}
          >
            {chip.label}
          </button>
        ) : (
          <Link
            key={chip.label}
            to={map[chip.to]}
            className="rounded-full border border-border bg-transparent px-2.5 py-1 text-[11px] tracking-[0.02em] text-muted-foreground no-underline hover:border-white/25 hover:bg-white/[0.08] hover:text-foreground"
          >
            {chip.label}
          </Link>
        ),
      )}
    </div>
  );
}

function ThinkRail({
  onExpand,
  jobs = [],
  onSelectJob,
}: {
  onExpand: () => void;
  jobs?: readonly ThinkJob[];
  onSelectJob?: (job: ThinkJob) => void;
}) {
  return (
    <div className="flex h-full w-12 flex-col items-center gap-2 bg-card pt-3">
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="flex flex-col items-center gap-2.5 text-muted-foreground transition-colors hover:text-foreground"
            aria-label="Expand Think"
            data-mission-id="panel:think"
            data-mission-panel="think"
            onClick={onExpand}
          >
            <span className="grid size-[30px] place-items-center rounded-[2px] border border-border font-mono text-[9px] font-bold tracking-[0.06em] hover:border-white/35 hover:text-foreground">
              TH
            </span>
            <span className="size-1.5 rounded-full bg-falcon" aria-hidden />
          </button>
        </TooltipTrigger>
        <TooltipContent side="left">Expand Think</TooltipContent>
      </Tooltip>
      <ThinkJobsPill jobs={jobs} onSelectJob={onSelectJob} />
    </div>
  );
}

function ThinkHeader({
  onCollapse,
  jobs = [],
  onSelectJob,
}: {
  onCollapse?: () => void;
  jobs?: readonly ThinkJob[];
  onSelectJob?: (job: ThinkJob) => void;
}) {
  return (
    <div className="flex h-12 shrink-0 items-center gap-2 border-b border-border px-3">
      <span className="grid size-7 place-items-center rounded-[2px] border border-border font-mono text-[9px] font-bold tracking-[0.06em] text-foreground">
        TH
      </span>
      <span className="text-[12px] font-semibold tracking-[0.06em] text-foreground uppercase">Think</span>
      <div className="ml-auto flex items-center gap-1.5">
        <ThinkJobsPill jobs={jobs} onSelectJob={onSelectJob} />
        {onCollapse && (
          <button
            type="button"
            className="rounded-full border border-border px-2.5 py-0.5 text-[11px] tracking-[0.04em] text-muted-foreground uppercase hover:bg-white/[0.08] hover:text-foreground"
            aria-label="Collapse Think"
            onClick={onCollapse}
          >
            Collapse
          </button>
        )}
      </div>
    </div>
  );
}

function ThinkGuide() {
  const { reviewId } = useParams();
  const attachments = usePromptInputAttachments();
  const blurb = reviewId
    ? "Walk findings on the schematic, or pull another zip if this isn’t the SoT you meant."
    : "Ask about this project stage, or open a deep-link below.";
  return (
    <div className="flex w-full flex-col items-stretch gap-3 p-3 text-left">
      <div className="rounded-[4px] border border-border bg-[#111] p-3 text-[13px] leading-snug text-foreground">
        <div className="mb-1.5 text-[10px] font-semibold tracking-[0.08em] text-[#545457] uppercase">
          Assistant
        </div>
        {blurb}
        <DeepLinks className="mt-2.5" />
      </div>
      <SuggestionChips onUploadZip={() => attachments.openFileDialog()} />
    </div>
  );
}

function ThinkDropZone({ children }: { children: ReactNode }) {
  const attachments = usePromptInputAttachments();
  const zoneRef = useRef<HTMLDivElement>(null);
  const dragCount = useRef(0);
  const [hover, setHover] = useState(false);
  const [hoverName, setHoverName] = useState<string | null>(null);
  const [reject, setReject] = useState<ThinkDropInputState>({
    draft: { text: "" },
    error: null,
    existingArtifactId: null,
    existingPathCount: null,
  });

  const onDragEnter = (event: DragEvent) => {
    event.preventDefault();
    if (!event.dataTransfer?.types?.includes("Files")) return;
    dragCount.current += 1;
    setHover(true);
    setHoverName(thinkDropHoverName(event.dataTransfer));
  };
  const onDragLeave = (event: DragEvent) => {
    event.preventDefault();
    if (!event.dataTransfer?.types?.includes("Files")) return;
    dragCount.current = Math.max(0, dragCount.current - 1);
    if (dragCount.current === 0) {
      setHover(false);
      setHoverName(null);
    }
  };
  const onDragOver = (event: DragEvent) => {
    event.preventDefault();
    if (!event.dataTransfer?.types?.includes("Files")) return;
    const name = thinkDropHoverName(event.dataTransfer);
    if (name) setHoverName(name);
  };
  const dropOnce = useRef<DragEvent | null>(null);
  const onDrop = (event: DragEvent) => {
    event.preventDefault();
    if (dropOnce.current === event) return;
    dropOnce.current = event;
    if (!event.dataTransfer?.types?.includes("Files")) return;
    dragCount.current = 0;
    setHover(false);
    setHoverName(null);
    const files = [...(event.dataTransfer.files ?? [])];
    if (files.length === 0) return;
    const file = files[0]!;
    const checked = validateThinkZipFile(file);
    if (!checked.ok) {
      setReject((prev) => applyThinkDropRejection(prev, checked.message));
      return;
    }
    setReject((prev) => ({ ...prev, error: null, existingArtifactId: null, existingPathCount: null }));
    attachments.add([file]);
  };

  useEffect(() => attachThinkDropSurface(zoneRef.current), []);

  return (
    <ThinkRejectContext.Provider value={{ state: reject, setState: setReject }}>
      <div
        ref={zoneRef}
        className="relative flex h-full min-h-0 flex-1 flex-col"
        data-testid="think-drop-zone"
        onDragEnter={onDragEnter}
        onDragLeave={onDragLeave}
        onDragOver={onDragOver}
        onDragOverCapture={onDragOver}
        onDrop={onDrop}
      >
        <div
          data-testid="think-drop-catcher"
          className="absolute inset-0 z-20"
          style={{ pointerEvents: hover ? "auto" : "none" }}
          onDragEnter={onDragEnter}
          onDragOver={onDragOver}
          onDrop={onDrop}
        />
        <ThinkDropOverlay active={hover} fileName={hoverName} />
        {children}
      </div>
    </ThinkRejectContext.Provider>
  );
}

function ThinkExpanded({
  onCollapse,
  jobs = [],
  onJobsChange,
  onSelectJob,
}: {
  onCollapse?: () => void;
  jobs?: readonly ThinkJob[];
  onJobsChange?: (jobs: ThinkJob[]) => void;
  onSelectJob?: (job: ThinkJob) => void;
}) {
  const { orgSlug, projectSlug, project } = useProjectScope();
  const params = useParams();
  const org = orgSlug || params.orgSlug;
  const proj = projectSlug || params.projectSlug;
  const remembered = rememberThinkProjectId(org, proj, project?.id);
  const keepChat = useRef(false);
  if ((org && proj) || remembered) keepChat.current = true;
  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      <ThinkHeader onCollapse={onCollapse} jobs={jobs} onSelectJob={onSelectJob} />
      <PageAgentBar />
      <ErrorBoundary
        fallback={
          <>
            <div className="min-h-0 flex-1 overflow-auto">
              <ThinkGuide />
            </div>
            <ThinkComposer />
          </>
        }
      >
        {keepChat.current ? (
          <AgentChat onJobsChange={onJobsChange} />
        ) : (
          <div className="min-h-0 flex-1 overflow-auto">
            <ThinkGuide />
          </div>
        )}
      </ErrorBoundary>
    </div>
  );
}

function ThinkComposer({
  busy,
  status,
  askUser,
  streamError,
  onDismissError,
  onStop,
  onSubmitText,
}: {
  busy?: boolean;
  status?: "ready" | "submitted" | "streaming" | "error";
  askUser?: boolean;
  streamError?: string | null;
  onDismissError?: () => void;
  onStop?: () => void;
  onSubmitText?: (text: string, files: ReturnType<typeof thinkFilePart>[]) => Promise<void>;
}) {
  const { orgSlug, projectSlug, reviewId } = useParams();
  const attachments = usePromptInputAttachments();
  const { state: panelReject, setState: setPanelReject } = useThinkReject();
  const [progress, setProgress] = useState<Record<string, { loaded: number; total: number }>>({});
  const [artifactById, setArtifactById] = useState<Record<string, { id: string; pathCount: number }>>(
    {},
  );
  const kept = useStagedThinkZip(orgSlug, projectSlug);
  const started = useRef(new Set<string>());
  const acceptedExisting = useRef(new Set<string>());
  const routeRef = useRef(orgSlug && projectSlug ? thinkStagedZipKey(orgSlug, projectSlug) : "");
  routeRef.current = orgSlug && projectSlug ? thinkStagedZipKey(orgSlug, projectSlug) : "";

  useEffect(
    () =>
      listenThinkZipRejection((message) => {
        setPanelReject((prev) => applyThinkDropRejection(prev, message));
      }),
    [setPanelReject],
  );

  useEffect(() => {
    if (attachments.files.length === 0 && panelReject.error) {
      setPanelReject((prev) => ({
        ...prev,
        error: null,
        existingArtifactId: null,
        existingPathCount: null,
      }));
    }
  }, [attachments.files.length, panelReject.error, setPanelReject]);

  useEffect(() => {
    if (!orgSlug || !projectSlug) return;
    for (const part of attachments.files) {
      if (started.current.has(part.id) || artifactById[part.id]) continue;
      started.current.add(part.id);
      void (async () => {
        try {
          const res = await fetch(part.url);
          const blob = await res.blob();
          const file = new File([blob], part.filename || "upload.zip", {
            type: part.mediaType || "application/zip",
          });
          const checked = validateThinkZipFile(file);
          if (!checked.ok) {
            setPanelReject((prev) => applyThinkDropRejection(prev, checked.message));
            attachments.remove(part.id);
            started.current.delete(part.id);
            return;
          }
          setProgress((p) => ({ ...p, [part.id]: { loaded: 0, total: file.size } }));
          const staged = await stageHardwareZip(orgSlug, projectSlug, file, {
            onProgress: (p) => setProgress((prev) => ({ ...prev, [part.id]: p })),
          });
          if (staged.ok) {
            setStagedThinkZip({
              org: orgSlug,
              project: projectSlug,
              filename: file.name,
              artifactId: staged.artifact_id,
              pathCount: staged.path_count,
            });
            if (routeRef.current !== thinkStagedZipKey(orgSlug, projectSlug)) return;
            setArtifactById((m) => ({
              ...m,
              [part.id]: { id: staged.artifact_id, pathCount: staged.path_count },
            }));
            setProgress((p) => ({
              ...p,
              [part.id]: { loaded: file.size, total: file.size },
            }));
            setPanelReject((prev) => ({
              ...prev,
              error: null,
              existingArtifactId: null,
              existingPathCount: null,
            }));
            return;
          }
          if (staged.status === 409 && staged.artifact_id) {
            if (acceptedExisting.current.has(part.id)) {
              setArtifactById((m) => ({
                ...m,
                [part.id]: { id: staged.artifact_id!, pathCount: staged.path_count ?? 0 },
              }));
              return;
            }
            setPanelReject((prev) =>
              applyThinkDropRejection(
                prev,
                THINK_DROP_REJECT.duplicate,
                staged.artifact_id ?? null,
                staged.path_count ?? null,
              ),
            );
            setProgress((p) => {
              const next = { ...p };
              delete next[part.id];
              return next;
            });
            return;
          }
          setPanelReject((prev) => applyThinkDropRejection(prev, staged.message));
        } catch (err) {
          setPanelReject((prev) =>
            applyThinkDropRejection(prev, err instanceof Error ? err.message : "upload failed"),
          );
        }
      })();
    }
  }, [attachments, attachments.files, artifactById, orgSlug, projectSlug]);

  const error = panelReject.error;
  const existingId = panelReject.existingArtifactId;
  const existingPathCount = panelReject.existingPathCount;

  return (
    <div className="shrink-0 border-t border-border p-2.5" data-mission-ignore="">
      {error && (
        <Alert
          variant="destructive"
          className="mb-2 border"
          data-testid="think-drop-alert"
          style={{
            color: THINK_REJECT_TEXT,
            background: THINK_REJECT_BG,
            borderColor: THINK_REJECT_BORDER,
          }}
        >
          <AlertTitle style={{ color: THINK_REJECT_TEXT }}>Upload rejected</AlertTitle>
          <AlertDescription
            className="flex flex-wrap items-center gap-2"
            style={{ color: THINK_REJECT_TEXT }}
          >
            <span>{error}</span>
            {existingId && (
              <Button
                size="xs"
                variant="outline"
                data-testid="think-use-existing"
                onClick={() => {
                  const part = attachments.files[0];
                  const pathCount = existingPathCount ?? 0;
                  if (part) {
                    acceptedExisting.current.add(part.id);
                    setArtifactById((m) => ({
                      ...m,
                      [part.id]: { id: existingId, pathCount },
                    }));
                    if (orgSlug && projectSlug) {
                      setStagedThinkZip({
                        org: orgSlug,
                        project: projectSlug,
                        filename: part.filename || "upload.zip",
                        artifactId: existingId,
                        pathCount,
                      });
                    }
                  }
                  setPanelReject((prev) => ({
                    ...prev,
                    error: null,
                    existingArtifactId: null,
                    existingPathCount: null,
                  }));
                }}
              >
                Use existing
              </Button>
            )}
          </AlertDescription>
        </Alert>
      )}
      <PromptInput
        className="rounded-[4px] border-border bg-black"
        accept={THINK_ZIP_ACCEPT}
        maxFileSize={THINK_ZIP_MAX_BYTES}
        validateFile={validateThinkZipFile}
        globalDrop={false}
        maxFiles={1}
        onError={(err) => {
          setPanelReject((prev) => applyThinkDropRejection(prev, err.message));
        }}
        onSubmit={async (message) => {
          const text = message.text.trim();
          const attachmentParts = attachments.files
            .map((f) => {
              const id = artifactById[f.id]?.id;
              return id ? thinkFilePart(id, f.filename || "upload.zip") : null;
            })
            .filter((p): p is ReturnType<typeof thinkFilePart> => p !== null);
          const parts = takeThinkSendFileParts(attachmentParts, kept, orgSlug, projectSlug);
          if (!text && parts.length === 0) return;
          await onSubmitText?.(text, parts);
        }}
      >
        <PromptInputBody>
          {streamError ? (
            <Alert variant="destructive" className="mx-2 mt-2" data-testid="think-turn-error">
              <AlertTitle>Think could not reply</AlertTitle>
              <AlertDescription className="flex flex-wrap items-center justify-between gap-2">
                <span>{streamError}</span>
                {onDismissError ? (
                  <Button type="button" size="sm" variant="outline" onClick={onDismissError}>
                    Dismiss
                  </Button>
                ) : null}
              </AlertDescription>
            </Alert>
          ) : null}
          {(attachments.files.length > 0 || kept) && (
            <div className="flex flex-wrap justify-start gap-1.5 px-2 pt-2">
              {dedupeThinkZipChips(attachments.files).map((file) => {
                const pct = progress[file.id];
                const staged = artifactById[file.id];
                const ready = Boolean(staged);
                const ratio =
                  pct && pct.total > 0 ? Math.round((pct.loaded / pct.total) * 100) : pct ? 0 : null;
                return (
                  <div
                    key={file.id}
                    data-testid="think-zip-chip"
                    className="flex max-w-full items-center justify-start gap-1.5 self-start rounded-full border border-border px-2 py-0.5 text-[11px]"
                  >
                    <span className="max-w-[140px] truncate">{file.filename}</span>
                    {pct != null && !ready && (
                      <>
                        <Progress
                          value={ratio ?? 0}
                          className="h-1 w-14"
                          data-testid="think-zip-progress"
                        />
                        <span
                          data-testid="think-zip-progress-label"
                          className="font-mono text-[10px] text-muted-foreground"
                        >
                          {formatUploadProgress(pct.loaded, pct.total)}
                        </span>
                      </>
                    )}
                    {ready && staged && (
                      <>
                        <Check className="size-3" style={{ color: THINK_OK }} aria-hidden />
                        <span className="font-mono text-[10px] text-foreground">{staged.id}</span>
                        {staged.pathCount > 0 ? (
                          <span className="text-[10px] text-muted-foreground">
                            {staged.pathCount} paths
                          </span>
                        ) : null}
                      </>
                    )}
                    <button
                      type="button"
                      aria-label="Remove attachment"
                      className="text-muted-foreground hover:text-foreground"
                      onClick={() => {
                        attachments.remove(file.id);
                        if (orgSlug && projectSlug) clearStagedThinkZip(orgSlug, projectSlug);
                      }}
                    >
                      <XIcon className="size-3" />
                    </button>
                  </div>
                );
              })}
              {dedupeThinkZipChips(attachments.files).length === 0 && kept ? (
                <div
                  data-testid="think-zip-chip"
                  className="flex max-w-full items-center justify-start gap-1.5 self-start rounded-full border border-border px-2 py-0.5 text-[11px]"
                >
                  <span className="max-w-[140px] truncate">{kept.filename}</span>
                  <Check className="size-3" style={{ color: THINK_OK }} aria-hidden />
                  <span className="font-mono text-[10px] text-foreground">{kept.artifactId}</span>
                  {kept.pathCount > 0 ? (
                    <span className="text-[10px] text-muted-foreground">{kept.pathCount} paths</span>
                  ) : null}
                  <button
                    type="button"
                    aria-label="Remove attachment"
                    className="text-muted-foreground hover:text-foreground"
                    onClick={() => {
                      if (orgSlug && projectSlug) clearStagedThinkZip(orgSlug, projectSlug);
                    }}
                  >
                    <XIcon className="size-3" />
                  </button>
                </div>
              ) : null}
            </div>
          )}
          <PromptInputTextarea
            className="min-h-[40px] text-[13px]"
            placeholder={
              askUser
                ? "Answer Think…"
                : reviewId
                  ? "Ask Think about this review…"
                  : "Ask Think…"
            }
          />
        </PromptInputBody>
        <PromptInputFooter className="flex-wrap items-end justify-between gap-x-2 gap-y-1">
          <ThinkZipPicker />
          <span
            className="shrink-0 whitespace-nowrap font-mono text-[11px]"
            style={{ color: THINK_HINT_COLOR }}
            data-testid="think-composer-status"
            data-official-busy={busy ? "1" : "0"}
          >
            {thinkComposerStatus(status ?? (busy ? "streaming" : "ready"))}
          </span>
          <PromptInputSubmit
            className="shrink-0"
            status={busy && status !== "error" ? "streaming" : status}
            onStop={onStop}
          />
        </PromptInputFooter>
      </PromptInput>
    </div>
  );
}

function AgentChat({ onJobsChange }: { onJobsChange?: (jobs: ThinkJob[]) => void }) {
  const { orgSlug, projectSlug, project } = useProjectScope();
  const projectId = rememberThinkProjectId(orgSlug, projectSlug, project?.id);
  // Keep the session mounted across Issues↔Files loading flicker so
  // useAgent / PartySocket is not torn down (QA r17: 2 new sockets).
  // Deep-link `?file=` and Think coexist: do not wait for idle hydrate
  // before mounting AgentChatSession — that ThinkGuide-only dead end
  // blocked open_file (P1).
  if (!thinkSessionMounts(projectId)) {
    return (
      <Conversation className="min-h-0 bg-card">
        <ConversationContent className="gap-3 p-3">
          <ConversationEmptyState className="p-0" title="" description="">
            <ThinkGuide />
          </ConversationEmptyState>
        </ConversationContent>
      </Conversation>
    );
  }
  return (
    <AgentChatSession
      projectId={projectId}
      orgSlug={orgSlug}
      projectSlug={projectSlug}
      onJobsChange={onJobsChange}
    />
  );
}

function AgentChatSession({
  projectId,
  orgSlug,
  projectSlug,
  onJobsChange,
}: {
  projectId: string;
  orgSlug: string | undefined;
  projectSlug: string | undefined;
  onJobsChange?: (jobs: ThinkJob[]) => void;
}) {
  const { reviewId, revId } = useParams();
  const { role } = useProjectScope();
  const location = useLocation();
  const navigate = useNavigate();
  const attachments = usePromptInputAttachments();
  const [socket, setSocket] = useState<"connecting" | "connected" | "disconnected">("connecting");
  const [agentHydrate, setAgentHydrate] = useState<ReturnType<typeof hydrateFromAgentState>>(null);
  const [agentChatTurnError, setAgentChatTurnError] = useState<
    ReturnType<typeof chatTurnErrorFromAgentState>
  >(null);
  const [askUser, setAskUser] = useState<(AskUserPending & { toolCallId: string }) | null>(null);
  const [stoppedNote, setStoppedNote] = useState(false);
  const [historyHasMore, setHistoryHasMore] = useState(false);
  const [olderPages, setOlderPages] = useState<Array<Array<{ id?: string; role?: string; parts?: unknown }>>>([]);
  const [loadingEarlier, setLoadingEarlier] = useState(false);
  const [liveJobs, setLiveJobs] = useState<Record<string, ThinkJob>>({});
  const [d1Jobs, setD1Jobs] = useState<Record<string, ThinkJob>>({});
  const [needsHumanConsumedLocal, setNeedsHumanConsumedLocal] = useState<Set<string>>(() => new Set());
  const d1JobsRef = useRef<Record<string, ThinkJob>>({});
  const askUserRef = useRef<(AskUserPending & { toolCallId: string }) | null>(null);
  askUserRef.current = askUser;
  const roleRef = useRef(role);
  roleRef.current = role;

  const navigator = useMemo(() => {
    const projectBase = orgSlug && projectSlug ? `/o/${orgSlug}/p/${projectSlug}` : null;
    return new MissionNavigator(
      createBrowserMissionDeps({
        getProjectBase: () => projectBase,
        getRole: () => roleRef.current,
        getLocation: () => ({
          pathname: window.location.pathname,
          search: window.location.search,
          hash: window.location.hash,
          title: document.title,
        }),
        navigate: (to) => navigate(to),
        getSelection: () => readWorkbenchSelection(),
      }),
    );
  }, [navigate, orgSlug, projectSlug, location.pathname, location.search, location.hash]);
  const navigatorRef = useRef(navigator);
  navigatorRef.current = navigator;
  const driveSessionRef = useRef<ReturnType<typeof createThinkMissionDriveSession> | null>(null);
  if (driveSessionRef.current == null) {
    driveSessionRef.current = createThinkMissionDriveSession();
  }
  const driveSession = driveSessionRef.current;
  const thinkChatBody = useCallback(
    (mode: PcbxTurnMode) => ({
      ...thinkChatBodyForMode(mode),
      ...thinkChatBodyViewRev({
        routeRevId: revId,
        pathname: location.pathname,
        search: location.search,
      }),
    }),
    [revId, location.pathname, location.search],
  );
  // Live onToolCall body count as React state so endTool/beginTool always
  // schedule a render. Official addToolOutput also setStates (agents
  // react.js 686 / 421–427 / 716–720 / 598–604); this covers any path where
  // inFlight hits 0 without that official commit.
  const [inFlight, setInFlight] = useState(0);

  const driveRequestIdRef = useRef<string | null>(null);
  const applyAgentJobs = useCallback((state: unknown) => {
    setAgentHydrate(hydrateFromAgentState(state));
    setAgentChatTurnError(chatTurnErrorFromAgentState(state));
    setLiveJobs(unionThinkJobs(jobsFromAgentState(state), d1JobsRef.current));
    const drive = driveFromAgentState(state);
    if (drive?.requestId) driveRequestIdRef.current = drive.requestId;
    if (drive?.cancelled) setStoppedNote(true);
    driveSessionRef.current?.onServerDrive(drive);
  }, []);
  const refreshD1Jobs = useCallback(() => {
    if (!orgSlug || !projectSlug) return;
    void listJobs(orgSlug, projectSlug)
      .then((body) => {
        const clocks = thinkJobsFromWorkbenchItems(body.items);
        d1JobsRef.current = clocks;
        setD1Jobs(clocks);
        setLiveJobs((prev) => unionThinkJobs(prev, clocks));
      })
      .catch(() => {
        /* D1 clocks are a backfill — WS state still renders. */
      });
  }, [orgSlug, projectSlug]);
  useEffect(() => {
    refreshD1Jobs();
  }, [refreshD1Jobs]);
  useEffect(() => {
    if (!orgSlug || !projectSlug) return;
    const onFocus = () => refreshD1Jobs();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [orgSlug, projectSlug, refreshD1Jobs]);
  const agentRef = useRef<ReturnType<typeof useAgent> | null>(null);
  const agent = useAgent({
    agent: THINK_AGENT,
    name: thinkAgentName(projectId),
    host: thinkAgentHost(),
    query:
      orgSlug && projectSlug
        ? { org: orgSlug, project: projectSlug }
        : undefined,
    onOpen: useCallback(() => {
      setSocket("connected");
      setLiveThinkAgent(agentRef.current);
      refreshD1Jobs();
    }, [refreshD1Jobs]),
    onClose: useCallback(() => setSocket("disconnected"), []),
    onError: useCallback(() => setSocket("disconnected"), []),
    onStateUpdate: applyAgentJobs,
  });
  agentRef.current = agent;
  useEffect(() => {
    setLiveThinkAgent(agent);
    return () => setLiveThinkAgent(null);
  }, [agent]);
  useEffect(() => {
    applyAgentJobs(agent.state);
  }, [agent.state, applyAgentJobs]);
  // Official useAgentChat / agentFetch: credentials include so get-messages
  // sends the Better Auth cookie on same-origin /agents (service binding).
  // https://developers.cloudflare.com/agents/runtime/communication/routing/
  useEffect(() => () => driveSession.dispose(), [driveSession]);
  const chat = useAgentChat({
    agent,
    credentials: "include",
    throttle: 100,
    // Official skip: do not `use()` get-messages (would suspend the session
    // and re-create the ThinkGuide-only dead end). Idle hydrate fills history.
    getInitialMessages: null,
    // Think persists messages in SQLite and ignores cf_agent_chat_messages
    // overwrites. Do not push the full transcript back over the 1 MiB WS limit.
    syncMessagesToServer: false,
    prepareSendMessagesRequest: lastMessageOnlyPrepareSend,
    onToolCall: async ({ toolCall, addToolOutput: addOutput }) => {
      const name = toolCall.toolName;
      if (name === "done") {
        driveSession.onTurnEnd();
        setInFlight(0);
        endMissionDrive();
        addOutput({
          toolCallId: toolCall.toolCallId,
          output: { ok: true, status: "done" },
        });
        return;
      }
      if (!(MISSION_NAV_CLIENT_TOOL_NAMES as readonly string[]).includes(name)) return;
      if (isThinkTurnStopped() || getMissionDrive().cancelling || getMissionDrive().paused || navigatorRef.current.paused) {
        qaR16StopTrace("onToolCall-deny", {
          name,
          toolCallId: toolCall.toolCallId,
          stopped: isThinkTurnStopped(),
          cancelling: getMissionDrive().cancelling,
        });
        addOutput({
          toolCallId: toolCall.toolCallId,
          output: {
            ok: false,
            error: isThinkTurnStopped() || getMissionDrive().cancelling ? "cancelled" : "paused",
            reason:
              isThinkTurnStopped() || getMissionDrive().cancelling
                ? "Stopped"
                : "Think is paused. Esc take over — human is driving.",
          },
          state: "output-error",
          errorText: isThinkTurnStopped() || getMissionDrive().cancelling ? "Stopped" : "paused",
        });
        return;
      }
      driveSession.beginTool(name, toolCall.input, toolCall.toolCallId);
      setInFlight(driveSession.inFlight());
      try {
        const result = await navigatorRef.current.dispatch(name, toolCall.input);
        if ("pending" in result && result.pending && result.tool === "ask_user") {
          const pending = { ...result, toolCallId: toolCall.toolCallId };
          askUserRef.current = pending;
          setAskUser(pending);
          return;
        }
        if (isThinkTurnStopped() || getMissionDrive().cancelling) {
          addOutput({
            toolCallId: toolCall.toolCallId,
            output: { ok: false, error: "cancelled", reason: "Stopped" },
            state: "output-error",
            errorText: "Stopped",
          });
          return;
        }
        addOutput({ toolCallId: toolCall.toolCallId, output: result });
      } finally {
        driveSession.endTool();
        setInFlight(driveSession.inFlight());
      }
    },
  });
  const { messages, sendMessage, status, addToolOutput, error, clearError } = chat;
  const needsHumanConsumed = useMemo(() => {
    const merged = loadNeedsHumanConsumed(projectId);
    for (const id of needsHumanConsumedFromMessages(messages)) merged.add(id);
    for (const id of needsHumanConsumedLocal) merged.add(id);
    return merged;
  }, [projectId, messages, needsHumanConsumedLocal]);
  const [paintedMessageCount, setPaintedMessageCount] = useState(0);
  useEffect(() => {
    if (messages.length <= paintedMessageCount) {
      if (messages.length < paintedMessageCount) setPaintedMessageCount(messages.length);
      return;
    }
    let cancelled = false;
    void (async () => {
      await yieldPaint();
      if (cancelled) return;
      startTransition(() => {
        setPaintedMessageCount((n) => Math.min(messages.length, n + 6));
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [messages.length, paintedMessageCount]);
  const paintedMessages = messages.slice(0, paintedMessageCount);
  // Official useAgentChat replaces `messages` on every `cf_agent_chat_messages`
  // frame (agents/chat/react.js 773-783). Older pages stay out of that array.
  const olderMessages = flattenOlderPages(olderPages, messages) as typeof messages;
  const stopThink = "stop" in chat && typeof chat.stop === "function" ? chat.stop : undefined;
  const historyHydratedRef = useRef(false);
  const liveHeadRef = useRef<string | undefined>(undefined);
  const olderPagesRef = useRef(olderPages);
  olderPagesRef.current = olderPages;
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const firstLiveId = messages[0]?.id;
  useEffect(() => {
    historyHydratedRef.current = false;
    liveHeadRef.current = undefined;
    setOlderPages([]);
    setHistoryHasMore(false);
  }, [projectId]);
  useEffect(() => {
    if (historyHydratedRef.current) return;
    if (typeof firstLiveId !== "string" || !firstLiveId) return;
    let cancelled = false;
    const stopIdle = scheduleThinkHydrate(() => {
      if (cancelled || historyHydratedRef.current) return;
      void fetchThinkMessagesPage({
        name: thinkAgentName(projectId),
        host: thinkAgentHost(),
        limit: THINK_VISIBLE_MESSAGE_COUNT,
        method: "HEAD",
        before: firstLiveId,
      }).then((page) => {
        if (cancelled) return;
        historyHydratedRef.current = true;
        startTransition(() => {
          setHistoryHasMore(hasMoreFromThinkHeader(page.hasMoreHeader));
        });
      });
    });
    return () => {
      cancelled = true;
      stopIdle();
    };
  }, [projectId, firstLiveId]);
  useEffect(() => {
    if (typeof firstLiveId !== "string" || !firstLiveId) return;
    const prevLive0 = liveHeadRef.current;
    liveHeadRef.current = firstLiveId;
    if (!prevLive0 || prevLive0 === firstLiveId) return;
    if (olderPagesRef.current.length === 0) return;
    let cancelled = false;
    void backfillOlderPagesUntilConnected({
      olderPages: olderPagesRef.current,
      live: messagesRef.current,
      fetchPage: (before) =>
        fetchThinkMessagesPage({
          name: thinkAgentName(projectId),
          host: thinkAgentHost(),
          limit: THINK_VISIBLE_MESSAGE_COUNT,
          before,
        }),
    }).then((result) => {
      if (cancelled) return;
      startTransition(() => {
        setOlderPages(result.pages);
      });
    });
    return () => {
      cancelled = true;
    };
  }, [projectId, firstLiveId]);
  const busy = officialThinkIsBusy(chat);
  const officialStreaming = chat.isStreaming === true;
  const officialServerStreaming = chat.isServerStreaming === true;
  const officialToolContinuation = chat.isToolContinuation === true;
  const officialRecovering = chat.isRecovering === true;
  useLayoutEffect(() => {
    const dock = document.querySelector('[data-testid="think-dock"]');
    if (dock) {
      dock.setAttribute("data-official-busy", busy ? "1" : "0");
      dock.setAttribute("data-official-status", status ?? "");
      dock.setAttribute("data-official-streaming", officialStreaming ? "1" : "0");
      dock.setAttribute("data-official-server-streaming", officialServerStreaming ? "1" : "0");
      dock.setAttribute("data-official-tool-continuation", officialToolContinuation ? "1" : "0");
      dock.setAttribute("data-official-recovering", officialRecovering ? "1" : "0");
      dock.setAttribute("data-official-inflight", String(inFlight));
    }
    // One hide decision: this render's official busy + live inFlight.
    driveSession.onStreaming(busy);
  }, [
    driveSession,
    busy,
    status,
    officialStreaming,
    officialServerStreaming,
    officialToolContinuation,
    officialRecovering,
    inFlight,
  ]);
  useEffect(() => {
    let start = 0;
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].role === "user") {
        start = i + 1;
        break;
      }
    }
    const parts = messages
      .slice(start)
      .flatMap((message) => (message.role === "assistant" ? message.parts : []));
    driveSession.noteUiParts(parts);
  }, [driveSession, messages]);
  const [socketRetrying, setSocketRetrying] = useState(false);
  const rawStreamError =
    error?.message ||
    (chat.connectionError ? chat.connectionError.message || "Think connection failed" : null) ||
    (status === "error" ? "Think could not complete that turn." : null);
  const streamError = agentChatTurnError ? null : rawStreamError;
  const socketDisconnected = socket === "disconnected";

  useEffect(() => {
    const abortServer = () => {
      const requestId = driveRequestIdRef.current ?? getMissionDrive().requestId;
      const chatBusy = officialThinkIsBusy(chat);
      qaR16StopTrace("abortServer", {
        requestId,
        isToolContinuation: Boolean((chat as { isToolContinuation?: boolean }).isToolContinuation),
        isStreaming: Boolean((chat as { isStreaming?: boolean }).isStreaming),
        isServerStreaming: Boolean((chat as { isServerStreaming?: boolean }).isServerStreaming),
        busy: chatBusy,
        socketReadyState: (agentRef.current as { socket?: { readyState?: number } } | null)?.socket?.readyState ?? null,
        hasAgentSend: typeof agentRef.current?.send === "function",
      });
      // Official frame first, before Stopped chrome or useAgentChat.stop().
      // stop() is a no-op when `_activeServerTurnId` is null (early resume).
      let sent = false;
      for (let i = 0; i < 5 && !sent; i += 1) {
        sent = sendOfficialChatCancel(agentRef.current, requestId);
      }
      qaR16StopTrace("sendOfficialChatCancel", { requestId, sent });
      if (!getMissionDrive().cancelling) requestCancelMissionDrive("stop");
      setStoppedNote(true);
      void stopThink?.();
    };
    (window as unknown as { __pcbxStopThink?: () => void }).__pcbxStopThink = abortServer;
    const onPause = () => {
      navigatorRef.current.pause("esc");
      abortServer();
    };
    const onResume = () => {
      navigatorRef.current.resume();
    };
    const onCancel = () => {
      navigatorRef.current.pause("stop");
      abortServer();
    };
    window.addEventListener(MISSION_PAUSE_EVENT, onPause);
    window.addEventListener(MISSION_RESUME_EVENT, onResume);
    window.addEventListener(MISSION_CANCEL_EVENT, onCancel);
    const unregister = registerOfficialThinkStop(abortServer);
    return () => {
      unregister();
      window.removeEventListener(MISSION_PAUSE_EVENT, onPause);
      window.removeEventListener(MISSION_RESUME_EVENT, onResume);
      window.removeEventListener(MISSION_CANCEL_EVENT, onCancel);
    };
  }, [agent, stopThink]);

  const answerAskUser = useCallback(
    (answer: string) => {
      const pending = askUserRef.current;
      if (!pending) return false;
      addToolOutput({
        toolCallId: pending.toolCallId,
        output: { answered: true, answer, auto_confirmed: false },
      });
      askUserRef.current = null;
      setAskUser(null);
      return true;
    },
    [addToolOutput],
  );

  const knownJobs = useMemo(() => {
    const parts = messages.flatMap((message) => message.parts);
    return unionThinkJobs(collectJobsFromParts(parts, liveJobs), d1Jobs);
  }, [messages, liveJobs, d1Jobs]);
  const pillJobs = useMemo(() => sortJobsForPill(Object.values(knownJobs)), [knownJobs]);

  useEffect(() => {
    onJobsChange?.(pillJobs);
  }, [onJobsChange, pillJobs]);

  const prevJobsRef = useRef<Record<string, ThinkJob> | null>(null);
  useEffect(() => {
    const prev = prevJobsRef.current;
    prevJobsRef.current = knownJobs;
    if (!prev) return;
    for (const job of Object.values(knownJobs)) {
      if (job.kind !== "import_project") continue;
      if (isActiveJobState(job.state)) continue;
      if (prev[job.id]?.state === job.state) continue;
      window.dispatchEvent(
        new CustomEvent(HARDWARE_RELOAD_EVENT, { detail: { jobId: job.id, state: job.state } }),
      );
    }
  }, [knownJobs]);

  const openJob = useCallback(
    (job: ThinkJob) => {
      void navigatorRef.current.navigate({ route: `jobs/${job.id}` });
    },
    [],
  );
  const openReview = useCallback((job: ThinkJob) => {
    if (!job.reviewId) return;
    void navigatorRef.current.navigate({ route: `changes/${job.reviewId}` });
  }, []);
  const cancelJobViaThink = useCallback(
    (job: ThinkJob) => {
      void sendMessage({ text: cancelViaThinkText(job), ...thinkChatBody("qa") });
    },
    [sendMessage, thinkChatBody],
  );
  const retryJobViaThink = useCallback(
    (job: ThinkJob) => {
      void sendMessage({ text: retryViaThinkText(job), ...thinkChatBody("qa") });
    },
    [sendMessage, thinkChatBody],
  );

  return (
    <>
      <div
        hidden
        data-testid="think-official-flags"
        data-official-busy={busy ? "1" : "0"}
        data-official-status={status ?? ""}
        data-official-streaming={officialStreaming ? "1" : "0"}
        data-official-server-streaming={officialServerStreaming ? "1" : "0"}
        data-official-tool-continuation={officialToolContinuation ? "1" : "0"}
        data-official-recovering={officialRecovering ? "1" : "0"}
        data-official-inflight={String(inFlight)}
      />
      <Conversation
        className="min-h-0 bg-card"
        onDragOver={(event) => {
          event.preventDefault();
        }}
      >
        <ConversationContent className="gap-3 p-3 pb-14">
          {messages.length === 0 ? (
            <ConversationEmptyState className="p-0" title="" description="">
              <ThinkGuide />
            </ConversationEmptyState>
          ) : (
            <>
            {historyHasMore ? (
              <button
                type="button"
                className="w-full rounded-[4px] border border-border px-2 py-1.5 font-mono text-[11px] text-muted-foreground hover:bg-white/[0.06]"
                data-testid="think-load-earlier"
                disabled={loadingEarlier}
                onClick={() => {
                  if (loadingEarlier) return;
                  setLoadingEarlier(true);
                  void fetchThinkMessagesPage({
                    name: thinkAgentName(projectId),
                    host: thinkAgentHost(),
                    limit: THINK_VISIBLE_MESSAGE_COUNT,
                    before: olderPagesCursor(olderPages, messages),
                  })
                    .then((page) => {
                      startTransition(() => {
                        setHistoryHasMore(hasMoreFromThinkHeader(page.hasMoreHeader));
                        if (page.messages.length) {
                          setOlderPages((prev) => prependOlderPage(prev, page.messages, messages));
                        }
                      });
                    })
                    .finally(() => setLoadingEarlier(false));
                }}
              >
                {loadingEarlier ? "Loading earlier…" : "Load earlier messages"}
              </button>
            ) : null}
            {olderMessages.length > 0 ? <div data-testid="think-older-pages" hidden /> : null}
            <div
              data-testid="think-history-ids"
              data-ids={[...olderMessages, ...messages]
                .map((row) => row.id)
                .filter((id): id is string => typeof id === "string" && id.length > 0)
                .join(",")}
              hidden
            />
            {[
              ...olderMessages.map((message) => ({ message, older: true as const })),
              ...paintedMessages.map((message) => ({ message, older: false as const })),
            ].map(({ message, older }) => (
              <div
                key={message.id}
                data-testid={older ? "think-older-message" : undefined}
                data-think-older={older ? "1" : undefined}
                data-message-id={message.id}
              >
              <Message
                from={message.role}
                className={message.role === "user" ? "max-w-[92%]" : "max-w-full"}
              >
                <MessageContent
                  className={cn(
                    "text-[13px] leading-relaxed",
                    message.role === "user" &&
                      "border border-border bg-[#111] px-3 py-2 group-[.is-user]:bg-[#111]",
                  )}
                >
                  {message.role === "assistant" ? (
                    <ThinkActionSteps parts={message.parts} streaming={busy} />
                  ) : null}
                  {message.parts.map((part, i) => {
                    if (part.type === "text") {
                      if (isInternalWakeText(part.text)) {
                        return null;
                      }
                      return <MessageResponse key={i}>{part.text}</MessageResponse>;
                    }
                    if (part.type === "reasoning") {
                      return (
                        <Reasoning
                          key={i}
                          isStreaming={status === "streaming" && i === message.parts.length - 1}
                        >
                          <ReasoningTrigger />
                          <ReasoningContent>
                            {"text" in part ? String(part.text) : ""}
                          </ReasoningContent>
                        </Reasoning>
                      );
                    }
                    if (part.type === "file") {
                      const filePart = part as { filename?: string; url?: string };
                      return (
                        <p key={i} className="font-mono text-[11px] text-[var(--copper)]">
                          {filePart.filename || "attachment"}{" "}
                          {filePart.url?.startsWith("pcbx-artifact://") ? filePart.url : ""}
                        </p>
                      );
                    }
                    const wake = parsePcbxJobPart(part);
                    if (wake) {
                      return <JobWakeMarker key={i} wake={wake} />;
                    }
                    if (part.type === "dynamic-tool" || part.type.startsWith("tool-")) {
                      const toolPart = part as {
                        type: `tool-${string}` | "dynamic-tool";
                        state: "input-streaming" | "input-available" | "output-available" | "output-error";
                        input?: unknown;
                        output?: unknown;
                        errorText?: string;
                        toolName?: string;
                        toolCallId?: string;
                      };
                      const jobName = jobToolNameFromPart(toolPart.type, toolPart.toolName);
                      if (jobName && isThinkJobToolName(jobName) && jobName !== "cancel_job") {
                        const job = resolveJobForCard({
                          toolName: jobName,
                          input: toolPart.input,
                          output: toolPart.output,
                          liveJobs: knownJobs,
                        });
                        if (job) {
                          return (
                            <JobCard
                              key={job.id}
                              job={job}
                              onOpen={openJob}
                              onCancel={cancelJobViaThink}
                              onRetry={retryJobViaThink}
                              onOpenReview={job.reviewId ? openReview : undefined}
                            />
                          );
                        }
                      }
                      const surfaced = surfaceNonJobThinkToolPart(toolPart);
                      if (surfaced) {
                        if (surfaced.kind === "needs-human") {
                          const showRetry = canShowNeedsHumanRetry(
                            role,
                            surfaced.toolCallId,
                            needsHumanConsumed,
                          );
                          return (
                            <div
                              key={i}
                              className="rounded-[4px] border border-border bg-[#111] px-3 py-2 text-[12px]"
                              data-testid="think-needs-human"
                              data-tool={surfaced.toolName}
                              data-tool-call-id={surfaced.toolCallId}
                            >
                              <p className="font-mono text-[11px] text-[var(--falcon)]">
                                {surfaced.message}
                              </p>
                              {showRetry ? (
                                <button
                                  type="button"
                                  className="mt-2 rounded-[3px] border border-[var(--copper)]/50 bg-[#141414] px-2 py-1 font-mono text-[11px] text-foreground hover:bg-[#1a1a1a] disabled:opacity-50"
                                  data-testid="think-needs-human-retry"
                                  onClick={() => {
                                    const next = new Set(needsHumanConsumed);
                                    next.add(surfaced.toolCallId);
                                    setNeedsHumanConsumedLocal((prev) => {
                                      const local = new Set(prev);
                                      local.add(surfaced.toolCallId);
                                      return local;
                                    });
                                    persistNeedsHumanConsumed(projectId, next);
                                    void sendMessage({
                                      text: resumeWriteViaThinkText(surfaced.toolCallId),
                                      ...thinkChatBody("edit"),
                                    });
                                  }}
                                >
                                  Retry pending write
                                </button>
                              ) : null}
                            </div>
                          );
                        }
                        const testId =
                          surfaced.kind === "error" || surfaced.kind === "result-error"
                            ? "think-tool-error"
                            : "think-tool-result";
                        const tone =
                          surfaced.kind === "result-ok"
                            ? "text-muted-foreground"
                            : "text-[var(--falcon)]";
                        return (
                          <p
                            key={i}
                            className={cn("font-mono text-[11px]", tone)}
                            data-testid={testId}
                            data-tool={surfaced.toolName}
                          >
                            {surfaced.message}
                          </p>
                        );
                      }
                      return null;
                    }
                    return null;
                  })}
                </MessageContent>
              </Message>
              </div>
            ))}
            </>
          )}
          {stoppedNote ? (
            <p
              className="px-1 font-mono text-[11px] text-muted-foreground"
              data-testid="think-stopped-note"
            >
              Stopped
            </p>
          ) : null}
          {agentChatTurnError ? (
            <div
              className="mx-1 rounded-[4px] border border-[var(--falcon)]/50 bg-[#111] px-3 py-2 text-[12px] text-foreground"
              data-testid="think-chat-error-bubble"
              data-chat-error-kind={agentChatTurnError.kind}
            >
              <p className="font-mono text-[11px] text-[var(--falcon)]">Q&A turn stopped</p>
              <p className="mt-1 text-[11px] text-muted-foreground">{agentChatTurnError.message}</p>
            </div>
          ) : null}
          {agentHydrate && agentHydrate.ok === false && !agentHydrate.skipped ? (
            <div
              className="mx-1 rounded-[4px] border border-[var(--falcon)]/40 bg-[#111] px-3 py-2 text-[12px] text-foreground"
              data-testid="think-hydrate-failed"
            >
              <p className="font-mono text-[11px] text-[var(--falcon)]">Workspace hydrate failed</p>
              <p className="mt-1 text-[11px] text-muted-foreground">
                {agentHydrate.error?.trim() ||
                  "Platform snapshot did not load. Try sync_project or retry in a moment."}
              </p>
            </div>
          ) : null}
          {socketDisconnected ? (
            <div
              className="mx-1 rounded-[4px] border border-border bg-[#111] px-3 py-2 text-[12px] text-foreground"
              data-testid="think-socket-disconnected"
            >
              <p>Think 未连接</p>
              <button
                type="button"
                className="mt-1.5 rounded-[4px] border border-border px-2 py-1 font-mono text-[11px] hover:bg-white/[0.08]"
                disabled={socketRetrying}
                onClick={() => {
                  setSocketRetrying(true);
                  setSocket("connecting");
                  window.location.reload();
                }}
              >
                {socketRetrying ? "重试中…" : "重试连接"}
              </button>
            </div>
          ) : null}
        </ConversationContent>
        <ConversationScrollButton className="bottom-3 left-auto right-3 translate-x-0" />
      </Conversation>

      {messages.length > 0 && (
        <div className="shrink-0 space-y-2 border-t border-border px-3 py-2">
          <DeepLinks />
          <SuggestionChips onUploadZip={() => attachments.openFileDialog()} />
        </div>
      )}

      {askUser && (
        <div
          className="mx-2.5 mb-0 rounded-[4px] border border-border bg-[#111] p-2.5"
          data-testid="think-ask-user"
          role="dialog"
          aria-live="polite"
        >
          <p className="mb-1 text-[10px] font-semibold tracking-[0.08em] text-[var(--copper)] uppercase">
            Think is asking
          </p>
          <p className="text-[13px] leading-snug text-foreground">{askUser.question}</p>
          {askUser.choices && askUser.choices.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {askUser.choices.map((choice) => (
                <button
                  key={choice}
                  type="button"
                  className="rounded-full border border-border px-2.5 py-1 text-[11px] text-foreground hover:bg-white/[0.08]"
                  onClick={() => answerAskUser(choice)}
                >
                  {choice}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <ThinkComposer
        busy={busy}
        status={status}
        askUser={Boolean(askUser)}
        streamError={streamError}
        onDismissError={() => clearError?.()}
        onStop={() => invokeOfficialThinkStop()}
        onSubmitText={async (text, files) => {
          if (status === "error") clearError?.();
          if (text && answerAskUser(text)) return;
          setStoppedNote(false);
          driveSession.onUserMessage();
          setInFlight(0);
          if (getMissionDrive().paused || navigatorRef.current.paused) {
            resumeMissionDrive();
            navigatorRef.current.resume();
          }
          const payload = {
            text: text || (files[0] ? `Attached ${files[0].filename}` : ""),
            files,
            ...thinkChatBody("qa"),
          };
          await sendMessage(payload);
          if (orgSlug && projectSlug) clearStagedThinkZip(orgSlug, projectSlug);
        }}
      />
    </>
  );
}

function ThinkActionSteps({
  parts,
  streaming,
}: {
  parts: Array<{ type: string; input?: unknown; output?: unknown; state?: string; toolName?: string }>;
  streaming: boolean;
}) {
  const steps = parts.flatMap((part, i) => {
    if (part.type !== "dynamic-tool" && !part.type.startsWith("tool-")) return [];
    const name =
      part.type === "dynamic-tool"
        ? (part.toolName ?? "tool")
        : part.type.replace(/^tool-/, "");
    const state = part.state ?? "";
    // Do not stringify large tool outputs — label from name + short input only.
    const outputErr =
      state === "output-available" &&
      part.output != null &&
      typeof part.output === "object" &&
      typeof (part.output as { error?: unknown }).error === "string";
    const status =
      state === "output-error" || state === "output-denied" || outputErr
        ? ("complete" as const)
        : state === "output-available"
          ? ("complete" as const)
          : streaming
            ? ("active" as const)
            : ("complete" as const);
    return [{ key: `${name}-${i}`, label: formatMissionCall(name, part.input), status }];
  });
  if (steps.length === 0) return null;
  return (
    <ChainOfThought defaultOpen className="mb-2" data-testid="think-action-steps">
      <ChainOfThoughtHeader>Think steps</ChainOfThoughtHeader>
      <ChainOfThoughtContent>
        {steps.map((step) => (
          <ChainOfThoughtStep key={step.key} label={step.label} status={step.status} />
        ))}
      </ChainOfThoughtContent>
    </ChainOfThought>
  );
}
